import { useState } from 'react';
import { api } from '../api/client';
import type { Dashboard, Occurrence, Transaction } from '../api/types';
import { TransactionRow } from '../components/TransactionRow';
import { krw, percent } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../state';

const COLORS = ['#3d674e', '#91aa73', '#c8d59f', '#d6ba86', '#91aeaa', '#b6abc3', '#c4c8bf', '#7f9c8a', '#e0c9a6', '#a9b98f'];

export function DashboardPage() {
  const { period, dataVersion, navigate, openEntry } = useApp();
  const [level, setLevel] = useState<'category' | 'detail'>('category');
  const dashboard = useAsync(() => api.get<{ data: Dashboard }>('/dashboard', { from: period.start, to: period.end }).then((r) => r.data), [period.start, period.end, dataVersion]);
  const recent = useAsync(() => api.get<{ data: Transaction[] }>('/transactions', { from: period.start, to: period.end, limit: 5 }).then((r) => r.data), [period.start, period.end, dataVersion]);
  const pending = useAsync(
    () => api.get<{ data: Occurrence[] }>('/recurring-occurrences', { from: period.start, to: period.end }).then((r) => r.data.filter((o) => o.status === 'expected' || o.status === 'needs_review')),
    [period.start, period.end, dataVersion]
  );

  if (dashboard.error && !dashboard.data) return <ErrorBox message={dashboard.error.message} retry={dashboard.reload} />;
  const d = dashboard.data;
  if (!d) return <div className="empty">불러오는 중…</div>;
  const t = d.totals;

  // 원형 차트: 순지출이 양수인 항목만. 환불 우세 항목은 따로 표시한다.
  const slices = (level === 'category'
    ? d.expenseByCategory.map((c) => ({ key: c.categoryId, name: c.name, sub: null as string | null, net: c.netKrw, archived: c.archived, categoryId: c.categoryId, subcategoryId: null as string | null }))
    : d.expenseBySubcategory.map((c) => ({ key: c.subcategoryId, name: c.name, sub: c.categoryName, net: c.netKrw, archived: c.archived, categoryId: c.categoryId, subcategoryId: c.subcategoryId }))
  ).filter((s) => BigInt(s.net) > 0n);
  const refundDominant = d.expenseByCategory.filter((c) => c.refundDominant);
  let position = 0;
  const stops = slices.map((s, i) => {
    const start = position;
    position += percent(s.net, d.chartTotalKrw);
    return `${COLORS[i % COLORS.length]} ${start}% ${Math.min(position, 100)}%`;
  });

  const budget = d.budget.total;
  const topMerchant = d.topMerchants[0]?.netKrw;

  return (
    <section>
      <div className="metrics">
        <article className="metric dark">
          <span>선택 기간 수입</span>
          <strong>{krw(t.incomeKrw)}</strong>
          <small>{d.counts.income ? `${d.counts.income}건의 실제 입금 기록` : '수입을 기록하면 이곳에 모아드려요'}</small>
        </article>
        <article className="metric">
          <span>선택 기간 지출 (순지출)</span>
          <strong className={t.refundDominant ? 'income-price' : ''}>{krw(t.netExpenseKrw)}</strong>
          <small>
            {d.counts.expense}건 지출{BigInt(t.refundKrw) > 0n ? ` · 환불 ${krw(t.refundKrw)} 반영` : ''}
            {t.refundDominant ? ' · 환불 우세' : ''}
          </small>
        </article>
        <article className="metric">
          <span>수입 − 지출</span>
          <strong className={BigInt(t.balanceKrw) < 0n ? 'negative' : ''}>{krw(t.balanceKrw)}</strong>
          <small>기록된 금액의 차액 · 계좌 잔액과 다릅니다{BigInt(t.transferKrw) > 0n ? ` · 이체 ${krw(t.transferKrw)} 제외` : ''}</small>
        </article>
      </div>
      <div className="secondary-metrics">
        <article className="metric">
          <span>기간 예산 잔액</span>
          {budget ? (
            <>
              <strong className={BigInt(budget.remainingKrw) < 0n ? 'negative' : ''}>{krw(budget.remainingKrw)}</strong>
              <small>
                {budget.partial ? '기간 환산 예산' : '기간 예산'} {krw(budget.budgetKrw)}
                {budget.partial ? ' · 부분 월은 일할 계산' : ''}
              </small>
            </>
          ) : (
            <>
              <strong className="muted-strong">예산 미설정</strong>
              <button className="text" onClick={() => navigate('budgets')}>
                예산 정하기 →
              </button>
            </>
          )}
        </article>
        <article className="metric">
          <span>월 환산 정기 지출</span>
          <strong>{krw(d.recurring.monthlyEquivalentExpenseKrw)}</strong>
          <small>
            이 기간 미확정 예정 지출 {krw(d.recurring.expectedExpenseKrw)}
            {d.recurring.variableCount ? ` · 금액 미정 ${d.recurring.variableCount}건` : ''} · 실제 합계와 별도
          </small>
        </article>
      </div>

      <article className="card income-card">
        <div className="card-head">
          <div>
            <h2>수입도 나눠서 살펴보세요</h2>
            <p className="muted">실제 받은 돈만 집계합니다. 예정 수입은 포함하지 않아요.</p>
          </div>
          <button className="primary" onClick={() => openEntry({ kind: 'income' })}>
            ＋ 수입 기록
          </button>
        </div>
        <div className="income-breakdown">
          <div>
            <span>
              고정수입<small>급여 · 정기수당 · 연금</small>
            </span>
            <b>{krw(t.fixedIncomeKrw)}</b>
          </div>
          <div>
            <span>
              부가수입<small>부업 · 상여금 · 이자 · 중고판매</small>
            </span>
            <b>{krw(t.additionalIncomeKrw)}</b>
          </div>
        </div>
        {d.incomeBySubcategory.length ? (
          d.incomeBySubcategory.map((s) => (
            <button key={s.subcategoryId ?? 'none'} className="income-source" onClick={() => navigate('history', { kind: 'income', categoryId: s.categoryId ?? undefined, subcategoryId: s.subcategoryId ?? undefined })}>
              <span>{s.name}</span>
              <b>{krw(s.incomeKrw)}</b>
            </button>
          ))
        ) : (
          <p className="hint">선택 기간에 수입 기록이 없습니다. 월급이나 부수입을 추가해보세요.</p>
        )}
      </article>

      <div className="columns">
        <article className="card">
          <div className="card-head">
            <h2>어디에 가장 많이 썼나요?</h2>
            <select value={level} onChange={(e) => setLevel(e.target.value as 'category' | 'detail')} aria-label="분류 상세 수준" className="compact">
              <option value="category">대분류</option>
              <option value="detail">소분류</option>
            </select>
          </div>
          <div className="category-chart">
            <div className="donut" style={{ background: stops.length ? `conic-gradient(${stops.join(',')})` : '#edf0e8' }}>
              <div>
                <small>소비 합계</small>
                <b>{krw(d.chartTotalKrw)}</b>
              </div>
            </div>
            <div className="legend">
              {slices.length ? (
                slices.map((s, i) => (
                  <button key={`${s.key}`} onClick={() => navigate('history', { kind: 'expense', categoryId: s.categoryId ?? undefined, subcategoryId: s.subcategoryId ?? undefined })}>
                    <span className="dot" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="category-name">
                      {s.sub && <small>{s.sub}</small>}
                      {s.name}
                      {s.archived ? ' (삭제됨)' : ''}
                    </span>
                    <b>{krw(s.net)}</b>
                    <span className="category-percent">{Math.round(percent(s.net, d.chartTotalKrw))}%</span>
                  </button>
                ))
              ) : (
                <p className="muted">이 기간에는 지출 기록이 없어요. 다른 기간을 선택해보세요.</p>
              )}
            </div>
          </div>
          {refundDominant.length > 0 && (
            <p className="hint">
              환불 우세: {refundDominant.map((c) => `${c.name} ${krw(c.netKrw)}`).join(', ')} — 원형 차트에서는 빠지고 순지출 합계에는 반영됩니다.
            </p>
          )}
          <p className="hint">카테고리를 누르면 해당 내역을 볼 수 있어요.</p>
        </article>
        <article className="card quick">
          <span className="eyebrow">QUICK NOTE</span>
          <h2>들어온 돈, 쓴 돈, 바로 남겨보세요.</h2>
          <p>금액과 분류만 고르면 끝. 한 문장 자동 입력은 AI 연결(4단계) 후 제공됩니다.</p>
          <button className="primary" onClick={() => openEntry({ kind: 'expense' })}>
            ＋ 지출 기록하기
          </button>
          {pending.data && pending.data.length > 0 && (
            <div className="pending">
              <b>확인할 예정 항목 {pending.data.length}건</b>
              {pending.data.slice(0, 3).map((o) => (
                <small key={o.id}>
                  {o.scheduledDate} · {o.ruleName} · {o.expectedAmountKrw ? krw(o.expectedAmountKrw) : '금액 미정'}
                  {o.status === 'needs_review' ? ' · 분류 재선택 필요' : ''}
                </small>
              ))}
              <button className="text" onClick={() => navigate('recurring')}>
                반복 항목에서 확인 →
              </button>
            </div>
          )}
        </article>
      </div>

      <div className="columns lower">
        <article className="card">
          <div className="card-head">
            <h2>자주 돈을 쓴 곳</h2>
            <span className="muted">순지출 기준</span>
          </div>
          {d.topMerchants.length ? (
            d.topMerchants.slice(0, 5).map((m, i) => (
              <div className="rank" key={m.merchant}>
                <div className="rank-line">
                  <span>
                    {i + 1}　{m.merchant}
                  </span>
                  <b>{krw(m.netKrw)}</b>
                </div>
                <div className="track">
                  <i style={{ width: `${topMerchant ? percent(m.netKrw, topMerchant) : 0}%` }} />
                </div>
              </div>
            ))
          ) : (
            <p className="muted">지출을 기록해보세요.</p>
          )}
        </article>
        <article className="card">
          <div className="card-head">
            <h2>최근 기록</h2>
            <button className="text" onClick={() => navigate('history', {})}>
              전체 보기 →
            </button>
          </div>
          {recent.data?.length ? recent.data.map((tx) => <TransactionRow key={tx.id} tx={tx} />) : <p className="muted">아직 기록이 없어요.</p>}
        </article>
      </div>
    </section>
  );
}

export function ErrorBox({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="empty">
      <p>{message}</p>
      <button onClick={retry}>다시 시도</button>
    </div>
  );
}
